import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Star, Facebook, Linkedin, Github, Youtube } from 'lucide-react';
import PortfolioAuditorLogo from './FundWiseLogo';

// Import new story-driven components
import StoryHero from './landing/StoryHero';
import JourneyTimeline from './landing/JourneyTimeline';
import TheDiscovery from './landing/TheDiscovery';
import DashboardMockup from './landing/DashboardMockup';
import MultiAssetBento from './landing/MultiAssetBento';

// Testimonials Carousel Component
const TestimonialsCarousel = () => {
  const [currentPage, setCurrentPage] = useState(0);
  
  const testimonials = [
    {
      name: "Anil",
      initial: "A",
      gradient: "from-blue-500 to-purple-500",
      rating: 5,
      testimonial: "What impressed me most about Portfolio Auditor is how it truly understands my investment journey. Instead of just showing generic past returns, it actually mimicked every purchase and redemption I made in my existing funds against my chosen benchmark."
    },
    {
      name: "Deepa", 
      initial: "D",
      gradient: "from-purple-500 to-pink-500",
      rating: 5,
      testimonial: "I've been investing for over a decade, and while my portfolio has grown, I always wondered if I could have done better. Portfolio Auditor's transaction mimicking feature finally gave me that answer."
    },
    {
      name: "Suresh",
      initial: "S",
      gradient: "from-orange-500 to-red-500",
      rating: 5,
      testimonial: "Finally, a tool that goes beyond simple trailing returns! Portfolio Auditor's ability to simulate my actual transaction history against a benchmark fund is brilliant."
    },
    {
      name: "Vikram",
      initial: "V",
      gradient: "from-cyan-500 to-blue-500",
      rating: 5,
      testimonial: "I've used other portfolio trackers, but none gave me the clarity of Portfolio Auditor. Simulating my exact purchase dates against a benchmark showed me the real story of my fund's performance."
    },
    {
      name: "Pooja",
      initial: "P",
      gradient: "from-green-500 to-teal-500",
      rating: 5,
      testimonial: "For long-term investors like me, understanding if you're truly beating the market requires more than just looking at past CAGR. Portfolio Auditor's transaction mimicking feature provides that crucial insight."
    },
    {
      name: "Meena",
      initial: "M",
      gradient: "from-pink-500 to-rose-500",
      rating: 5,
      testimonial: "I was quite surprised to see the difference when Portfolio Auditor mimicked my specific investment dates and amounts against a benchmark. A must-try for serious investors!"
    }
  ];

  const testimonialsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);
  const startIndex = currentPage * testimonialsPerPage;
  const currentTestimonials = testimonials.slice(startIndex, startIndex + testimonialsPerPage);

  return (
    <div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        {currentTestimonials.map((testimonial, index) => (
          <div key={startIndex + index} className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center space-x-4 mb-4">
              <div className={`w-12 h-12 bg-gradient-to-r ${testimonial.gradient} rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                {testimonial.initial}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">{testimonial.name}</h3>
                <div className="flex items-center">
                  {[...Array(testimonial.rating)].map((_, starIndex) => (
                    <Star key={starIndex} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-slate-700 leading-relaxed italic text-sm">"{testimonial.testimonial}"</p>
          </div>
        ))}
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center items-center space-x-2">
        {[...Array(totalPages)].map((_, pageIndex) => (
          <button
            key={pageIndex}
            onClick={() => setCurrentPage(pageIndex)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentPage === pageIndex
                ? 'bg-blue-600 scale-110'
                : 'bg-slate-300 hover:bg-slate-400'
            }`}
            aria-label={`Go to testimonials page ${pageIndex + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const pricingPlans = [
    {
      name: "Beta Free Trial",
      price: "FREE",
      period: `${process.env.REACT_APP_BETA_DURATION || '7-day trial'}`,
      tag: "TRY FOR FREE",
      description: `Experience full power of Portfolio Auditor **PC Desktop Tool** with **${process.env.REACT_APP_BETA_DURATION || '1-month free trial'} license**.`,
      featureSections: [
        {
          title: "Portfolio vs Benchmark",
          features: [
            "Single PAN Investment Analysis",
            "Portfolio vs Standard Benchmark",
            "Portfolio vs Custom Fund Benchmark",
            "Outperformance/Underperformance Visualization",
            "Clear Insights on underperforming Fund"
          ]
        },
        {
          title: "Daily Investment Intelligence",
          features: [
            "Daily Portfolio Tracking & Summary",
            "Tax Calculation & Rolling Return Analysis"
          ]
        }
      ],
      popular: false,
      beta: true,
      buttonText: `Get Free License for ${process.env.REACT_APP_BETA_PERIOD || '1 Month'}`
    },
    {
      name: "Monthly Plan",
      price: `₹${process.env.REACT_APP_INDIVIDUAL_PRICE || '199'}`,
      period: "/mo",
      tag: "BEST FOR SOLO INVESTORS",
      description: "**PC Desktop Tool License** - Monthly auto-renewal for individual portfolio analysis.",
      featureSections: [
        {
          title: "Portfolio vs Benchmark",
          features: [
            "Single PAN Investment Analysis",
            "Portfolio vs Standard Benchmark",
            "Portfolio vs Custom Fund Benchmark",
            "Full Visualization & Insights"
          ]
        },
        {
          title: "Support & Updates",
          features: [
            "Live Chat & Email Support",
            "Free Future Tool Enhancements"
          ]
        }
      ],
      popular: false,
      buttonText: `Get License for ₹${process.env.REACT_APP_INDIVIDUAL_PRICE || '199'}/mo`
    },
    {
      name: "Yearly Plan",
      price: `₹${process.env.REACT_APP_ANNUAL_PRICE || '699'}`,
      period: "/year",
      tag: "MAXIMUM SAVINGS",
      description: "**PC Desktop Tool License** - Annual subscription with maximum savings.",
      savings: `Save ₹${(12 * parseInt(process.env.REACT_APP_INDIVIDUAL_PRICE || '99')) - parseInt(process.env.REACT_APP_ANNUAL_PRICE || '699')}/year`,
      featureSections: [
        {
          title: "Multi-PAN Analysis",
          features: [
            `**Up to ${process.env.REACT_APP_ANNUAL_PAN_LIMIT || '3'} PAN Combined Analysis**`,
            "Family Portfolio vs Benchmark",
            "Consolidated Family Portfolio View"
          ]
        },
        {
          title: "Priority Support",
          features: [
            "Priority Support & Faster Response",
            "365 Days Validity"
          ]
        }
      ],
      popular: true,
      buttonText: `Get License for ₹${process.env.REACT_APP_ANNUAL_PRICE || '699'}/year`
    }
  ];

  return (
    <div className="min-h-screen bg-deep-space">
      {/* Navigation - Dark Theme */}
      <nav className="bg-deep-space/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Link 
                to="/" 
                className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                <PortfolioAuditorLogo size="md" variant="default" />
              </Link>
              <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                BETA
              </span>
            </div>
            
            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center space-x-8">
              <button 
                onClick={() => {
                  const element = document.getElementById('journey-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-slate-300 hover:text-white font-medium transition-colors duration-200"
              >
                My Story
              </button>
              <button 
                onClick={() => {
                  const element = document.getElementById('pricing-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-slate-300 hover:text-white font-medium transition-colors duration-200"
              >
                Pricing
              </button>
              <Link 
                to="/faq"
                className="text-slate-300 hover:text-white font-medium transition-colors duration-200"
              >
                FAQ
              </Link>
              <Link 
                to="/installation-guide"
                className="text-slate-300 hover:text-white font-medium transition-colors duration-200"
              >
                Installation Guide
              </Link>
            </div>

            {/* Desktop Action Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link 
                to="/login" 
                className="text-slate-300 hover:text-white font-medium transition-colors duration-200 px-4 py-2"
                data-testid="nav-login-link"
              >
                Login
              </Link>
              <Link 
                to="/login" 
                className="bg-gradient-to-r from-tech-blue to-blue-600 hover:from-blue-600 hover:to-tech-blue text-white px-6 py-2 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
                data-testid="nav-try-free-btn"
              >
                Get Free License
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-white"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t border-white/10 bg-deep-space">
              <div className="flex flex-col space-y-3">
                <button 
                  onClick={() => {
                    const element = document.getElementById('journey-section');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                    setIsMenuOpen(false);
                  }}
                  className="text-slate-300 hover:text-white font-medium px-4 py-2 text-left"
                >
                  My Story
                </button>
                <button 
                  onClick={() => {
                    const element = document.getElementById('pricing-section');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                    setIsMenuOpen(false);
                  }}
                  className="text-slate-300 hover:text-white font-medium px-4 py-2 text-left"
                >
                  Pricing
                </button>
                <Link 
                  to="/faq"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-slate-300 hover:text-white font-medium px-4 py-2"
                >
                  FAQ
                </Link>
                <Link 
                  to="/installation-guide"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-slate-300 hover:text-white font-medium px-4 py-2"
                >
                  Installation Guide
                </Link>
                <div className="border-t border-white/10 pt-3 mt-2">
                  <Link 
                    to="/login" 
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-slate-300 hover:text-white font-medium px-4 py-2"
                  >
                    Login
                  </Link>
                  <Link 
                    to="/login" 
                    onClick={() => setIsMenuOpen(false)}
                    className="block bg-gradient-to-r from-tech-blue to-blue-600 text-white px-4 py-3 rounded-xl font-semibold mx-4 mt-2 text-center"
                  >
                    Get Free License
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Story-Driven Sections */}
      <StoryHero />
      <JourneyTimeline />
      <TheDiscovery />
      <DashboardMockup />
      <MultiAssetBento />

      {/* Pricing Section */}
      <section id="pricing-section" className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3 font-outfit">Choose Your License Plan</h2>
            <p className="text-lg text-slate-600 font-ibm-plex">Get your PC desktop tool license - Monthly auto-renewal</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div key={index} className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
                plan.beta ? 'border-blue-500 transform scale-105' :
                plan.popular ? 'border-blue-600 transform scale-105' : 'border-slate-200'
              } p-6 flex flex-col h-full`}>
                
                {/* Tag/Badge */}
                <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white ${
                  plan.beta ? 'bg-blue-600' :
                  plan.popular ? 'bg-blue-600' : 'bg-slate-600'
                }`}>
                  {plan.tag}
                </div>
                
                <div className="flex-grow">
                  <h3 className="text-xl font-bold text-slate-900 mb-3 mt-3 text-center font-outfit">{plan.name}</h3>
                  
                  <p className="text-slate-600 mb-4 text-center text-sm leading-relaxed font-ibm-plex" 
                     dangerouslySetInnerHTML={{__html: plan.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}}></p>
                  
                  <div className="mb-4 text-center">
                    <span className={`text-4xl font-bold font-jetbrains ${
                      plan.beta ? 'text-blue-600' :
                      plan.popular ? 'text-blue-600' : 'text-slate-900'
                    }`}>
                      {plan.price}
                    </span>
                    <span className="text-slate-500 ml-2 text-base">{plan.period}</span>
                  </div>

                  {plan.savings && (
                    <div className="mb-4 text-center">
                      <span className="inline-block bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 text-xs font-bold px-2 py-1 rounded-full">
                        {plan.savings}
                      </span>
                    </div>
                  )}
                  
                  <div className="space-y-4 mb-6">
                    {plan.featureSections.map((section, sectionIdx) => (
                      <div key={sectionIdx}>
                        <h4 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide font-outfit">
                          {section.title}
                        </h4>
                        <ul className="space-y-1">
                          {section.features.map((feature, featureIdx) => (
                            <li key={featureIdx} className="flex items-start text-xs text-slate-600 font-ibm-plex">
                              <CheckCircle className={`w-3 h-3 mr-2 mt-0.5 flex-shrink-0 ${
                                plan.beta ? 'text-blue-500' :
                                plan.popular ? 'text-blue-500' : 'text-green-500'
                              }`} />
                              <span dangerouslySetInnerHTML={{__html: feature.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>')}}></span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-auto">
                  <Link 
                    to="/login" 
                    className={`w-full inline-flex items-center justify-center py-4 px-6 rounded-xl font-semibold text-sm transition-all duration-300 ${
                      plan.beta || plan.popular
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                        : 'bg-slate-600 hover:bg-slate-700 text-white shadow-lg hover:shadow-xl'
                    }`}
                    data-testid={`pricing-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                  >
                    {plan.buttonText || 'Get Started'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4 font-outfit">What Our Clients Say</h2>
            <p className="text-lg text-slate-600 font-ibm-plex">Hear from investors who have optimized their portfolios with our analysis</p>
          </div>
          
          <TestimonialsCarousel />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-tech-blue to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 font-outfit">
            Ready to Discover Your Portfolio's Real Performance?
          </h2>
          <p className="text-lg text-blue-100 mb-4 font-ibm-plex">
            Stop guessing. Start knowing. Get exact comparisons with your actual investment dates.
          </p>
          <p className="text-lg text-blue-200 mb-8 font-ibm-plex">
            Beta Launch: {process.env.REACT_APP_BETA_DURATION?.toLowerCase() || '7-day free trial'} • Complete privacy • Your data never leaves your PC
          </p>
          <Link 
            to="/login" 
            className="bg-white text-blue-600 font-semibold py-4 px-8 rounded-xl hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 inline-flex items-center font-outfit"
            data-testid="cta-get-started-btn"
          >
            Audit Your Portfolio Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-deep-space text-white py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Logo Section */}
            <div>
              <Link 
                to="/" 
                className="inline-block cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                <PortfolioAuditorLogo size="md" variant="default" />
              </Link>
              <p className="text-slate-400 mt-4 text-sm font-ibm-plex">
                Professional portfolio analysis tool that runs entirely on your PC. 
                Your financial data never leaves your computer.
              </p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-bold mb-4 font-outfit">Quick Links</h4>
              <ul className="space-y-2 text-sm font-ibm-plex">
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('pricing-section');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    Pricing
                  </button>
                </li>
                <li><Link to="/faq" className="text-slate-400 hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/installation-guide" className="text-slate-400 hover:text-white transition-colors">Installation Guide</Link></li>
                <li><Link to="/terms-and-conditions" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/shipping-policy" className="text-slate-400 hover:text-white transition-colors">shipping Policy</Link></li>
              </ul>
            </div>
            
            {/* Social Media Section */}
            <div>
              <h4 className="font-bold mb-4 font-outfit">Connect With Us</h4>
              <div className="flex space-x-4">
                <a 
                  href={process.env.REACT_APP_YOUTUBE_CHANNEL_URL || 'https://youtube.com/@portfolioauditor'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
                <a 
                  href="https://linkedin.com/company/portfolioauditor" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
              <p className="text-slate-400 text-sm mt-4 font-ibm-plex">
                Contact: <a href="mailto:support@portfolioauditor.com" className="hover:text-white transition-colors">support@portfolioauditor.com</a>
              </p>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-slate-400 text-sm font-ibm-plex">
            <p>© 2025 Portfolio Auditor. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
